import {Component, OnDestroy, OnInit, ViewChild}    from '@angular/core';
import {FormBuilder, FormGroup, Validators}         from '@angular/forms';
import {HttpErrorResponse}                          from "@angular/common/http";
import {NgbActiveModal}                             from '@ng-bootstrap/ng-bootstrap';

import {AngularQuery}                               from "../../services/query/angularquery";
import {College}                                    from "../../services/college/college";
import {ItemComponent}                              from "../../articles/item/item.component";
import {Message}                                    from "../../services/message/message";
import {MessageEntry}                               from "../../services/message/message-entry";
import {Player}                                     from '../../services/player/player';
import {PlayerService}                              from "../../services/player/player.service";
import {Position}                                   from "../../services/position/position";
import {PositionService}                            from "../../services/position/position.service";
import {Sport}                                      from '../../services/sport/sport';
import {SportService}                               from "../../services/sport/sport.service";
import {Team}                                       from "../../services/team/team";
import {Validate4Digits}                            from "../../validators/digits4.validator";
import {VendorLink}                                 from "../../services/vendor/vendor-link";
import {Vendor}                                     from "../../services/vendor/vendor";
import {VendorService}                              from "../../services/vendor/vendor.service";

import {isNullOrUndefined}                          from "util";
import {Subscription}                               from 'rxjs/Subscription';



@Component({
	selector: 'app-player-admin',
	templateUrl: './player-admin.component.html',
	styleUrls: ['./player-admin.component.css'],
})


/**
 * Angular 2 Reactive Forms administrator screen for
 * Player objects.
 */
export class PlayerAdminComponent implements OnInit, OnDestroy {

	private static DEFAULT_COLLEGE_ID = 275;  // This is the value for 'None' in the 'college' table.

	editIndex: number;
	editLink: VendorLink;
	editVendorValue: boolean = false;
	hideSearch: boolean = true;
	message:Message;
	pForm: FormGroup;
	player: Player;
	playerFound: Subscription;
	position: Position[] = [];
	positionFound: Subscription;
	sport: Sport[] = [];
	sportFound: Subscription;
	vendor: VendorLink[] = [];
	vendorFound: Subscription;
	vendorList: Vendor[] = [];
	vendorListFound: Subscription;


	/**
	 * Initialize the object.
	 *
	 * @param fb               The FormBuilder for the page.
	 * @param playerService    The PlayerService that retrieves Player data.
	 * @param positionService  The PositionService that retrieves Position data.
	 * @param sportService     The SportService that retrieves Sport data.
	 * @param vendorService    The VendorService that retrieves player vendor (VendorLink) data.
	 * @param activeModal      Used to make this a modal Component.
	 */
	constructor(
				private fb: FormBuilder,
	            private playerService:PlayerService,
	            private positionService: PositionService,
	            private sportService: SportService,
	            private vendorService: VendorService,
	            public activeModal: NgbActiveModal,
	) {}


	/**
	 * Clear any subscriptions created earlier.
	 */
	ngOnDestroy() {

		if (this.playerFound) {
			this.playerFound.unsubscribe();
		}

		if (this.positionFound) {
			this.positionFound.unsubscribe();
		}

		if (this.sportFound) {
			this.sportFound.unsubscribe();
		}

		if (this.vendorFound) {
			this.vendorFound.unsubscribe();
		}

		if (this.vendorListFound) {
			this.vendorListFound.unsubscribe();
		}

	}


	/**
	 * Initialize the form, then arrange for the information to fill it.
	 */
	ngOnInit() {

		// Set up the message window with an initial message.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Loading...';
		me.level = 1;
		this.message = new Message(me);
		this.message.show();

		// Initialize the data structure for the page.
		this.pForm = this.fb.group({
			draft_year: ['', [Validate4Digits]],
			firstname: ['', [Validators.required,Validators.minLength(1),Validators.maxLength(64),]],
			surname: ['', [Validators.required,Validators.minLength(1),Validators.maxLength(64),]],
			id: ['', [Validators.required, Validators.min(-1)]],
			college: [PlayerAdminComponent.DEFAULT_COLLEGE_ID, [Validators.required, Validators.min(0)]],
			code: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(3)]],
			team: ['', [Validators.required, Validators.min(0)]],
      position: ['', [Validators.required, Validators.min(0)]],
      status: ['active', [Validators.required, Validators.min(0)]],
			vendor_value: ['', []],
			create_edit: ['new', []],
		});

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.sportFound = this.sportService.getSports().subscribe(

			(response) => {

				// Pass back the array of Sport objects.
				this.sport = response;
				// Get the player data now.
				this.playerFound = this.playerService.getPlayerById(-1).subscribe(

					(player_response) => {

						this.player = new Player(player_response);
						this.pForm.patchValue(this.player);
						this.onSportChange(this.player.code);

					},

					() => {

						// Notify the user.
						me.message = 'Unable to load player information';
						me.level = 4;
						this.message.add(me, true);
						this.message.show();

					}

				);

			},
			() => {

				// Notify the user.
				me.message = 'Unable to load sports';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

		// Get the list of vendors.
		this.vendorListFound = this.vendorService.getVendors().subscribe(

			(response) => {

				if (Array.isArray(response)) {

					// Fill it with the new ones.
					for (let vendor of response) {

						this.vendorList.push(new Vendor(vendor));

					}

				} else {

					// Notify the user.
					me.message = 'Vendor data was invalid';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			},

			() => {

				// Notify the user.
				me.message = 'Unable to load vendor data';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Lets me talk to the College typeahead.
	 */
	@ViewChild('collegeTypeahead')
	collegeTypeahead: any;


	/**
	 * Lets me talk to the Player typeahead.
	 */
	@ViewChild('playerTypeahead')
	playerTypeahead: any;


	/**
	 * Lets me talk to the Team typeahead.
	 */
	@ViewChild('teamTypeahead')
	teamTypeahead: any;


	/**
	 * Lets me talk to the Vendor input.
	 */
	@ViewChild('vendorInput')
	vendorInput: any;


	/**
	 * Lets me talk to the Vendor Edit input.
	 */
	@ViewChild('vendorEditInput')
	vendorEditInput: any;


	/**
	 * Lets me talk to the Vendor no choice box.
	 */
	@ViewChild('vendorNoChoice')
	vendorNoChoice: any;


	/**
	 * Lets me talk to the Vendor select.
	 */
	@ViewChild('vendorSelect')
	vendorSelect: any;


	/**
	 * Add the current vendor entry to this player.
	 * This is a new entry; there are separate routines
	 * to handle editing existing entries.
	 */
	addVendorEntry():void {

		if (0 === this.vendorInput.nativeElement.value.trim().length) {
			let me:MessageEntry = new MessageEntry();
			me.message = 'Blank identifiers are not allowed in vendor entries';
			me.level = 4;
			this.message.add(me, true);
			this.message.show();
			return;
		}

		let entry:VendorLink = new VendorLink();
		entry.id = -1;
		entry.origin_id = -1;
		entry.vendor_link = this.vendorInput.nativeElement.value;
		entry.vendor_id = this.vendorSelect.nativeElement.value;
		entry.name = this.vendorSelect.nativeElement.selectedOptions[0].label;

		if (this.checkVendor(entry.vendor_id)) {
			let me:MessageEntry = new MessageEntry();
			me.message = 'Please edit the existing \'' + entry.name + '\' entry instead of making a new one.';
			me.level = 4;
			this.message.add(me, true);
			this.message.show();
			return;
		}

		this.vendor.push(entry);
		this.vendorInput.nativeElement.value = '';

		let me:MessageEntry = new MessageEntry();
		me.message = 'Added \'' + entry.name + '\' entry';
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * Cancel an existing edit of a vendor entry.
	 */
	cancelVendorEdit():void {

		this.editVendorValue = false;

	}


	/**
	 * Check the existing vendor list to see if that vendor
	 * already has an entry for this player.
	 *
	 * @param  {number} id  The vendor ID to search for.
	 * @return {boolean} True if an entry exists, false otherwise.
	 */
	checkVendor(id:number):boolean {

		let existingItem:VendorLink = this.vendor.find(obj => obj.vendor_id === id);
		return ! isNullOrUndefined(existingItem);

	}


	/**
	 * Clear the fields to get ready for another Player entry.
	 */
	clear(): void {

		// Check whether they are editing an existing player or whether we created and saved a new one.
		this.hideSearch = ('new' === this.pForm.controls['create_edit'].value);

		// Reset all the other fields.
		this.pForm.controls['draft_year'].setValue('');
		this.pForm.controls['firstname'].setValue('');
		this.pForm.controls['surname'].setValue('');
		this.pForm.controls['id'].setValue(-1);
		this.pForm.controls['team'].setValue(-1);
    this.pForm.controls['position'].setValue(0);
    this.pForm.controls['status'].setValue('active');
		this.pForm.controls['college'].setValue(PlayerAdminComponent.DEFAULT_COLLEGE_ID);
		this.collegeTypeahead.typeaheadCollegeInput.nativeElement.value = '';
		this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
		this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = '';
		this.vendor = [];
		this.vendorInput.nativeElement.value = '';
		this.vendorEditInput.nativeElement.value = '';

	}


	/**
	 * Delete the currently active player on the admin screen.
	 */
	deletePlayer(): void {

		if (-1 === this.pForm.controls['id'].value) {

			this.clear();
			return;

		}

		let name:string = this.pForm.controls['firstname'].value + ' ' + this.pForm.controls['surname'].value;
		if (false === confirm('Click OK to delete ' + name)) {

			return;

		}

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Deleting ' + name + ' ...';
		me.level = 1;
		this.message.add(me, true);
		this.message.show();

		if (this.playerFound) {

			this.playerFound.unsubscribe();

		}

		this.playerFound = this.playerService.deletePlayerById(this.pForm.controls['id'].value).subscribe(

			(response) => {

				this.clear();

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = 'Deleted ' + name;
				me.level = 2;
				this.message.add(me, true);
				this.message.show();

			},

			() => {

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = 'Unable to delete ' + name;
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Delete the specified vendor entry from the list.  This is the index of
	 * the entry in the current list, not the ID value of the entry.
	 *
	 * @param {number} index  The entry to remove from the list of vendor entries.
	 */
	deleteVendorEntry(index:number): void {

		if (this.editVendorValue) {

			window.alert('Finish or cancel your existing edit.');
			return;

		}

		this.vendor.splice(index, 1);

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Vendor entry removed from list';
		me.level = 2;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * Edit the specified vendor entry.  This is the index of
	 * the entry in the current list, not the ID value of the entry.
	 *
	 * @param {VendorLink} entry  The entry to edit in the list of vendor entries.
	 * @param {number} index  Index of the entry in the current list.
	 */
	editVendorEntry(entry:VendorLink, index:number): void {

		if (this.editVendorValue) {

			window.alert('Finish or cancel your existing edit.');
			return;

		}

		this.editIndex = index;
		this.editLink = entry;
		this.vendorNoChoice.nativeElement.value = this.editLink.name;
		this.vendorEditInput.nativeElement.value = this.editLink.vendor_link;
		this.editVendorValue = true;

		// Notify the user.
		let me:MessageEntry = new MessageEntry();
		me.message = 'Vendor entry selected for editing';
		me.level = 2;
		this.message.add(me, true);
		this.message.show();

	}


	/**
	 * This is used by the Create/Edit radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isCreateEditHighlighted(name: string): any {

		if (name === this.pForm.controls['create_edit'].value) {

			return {'color': 'blue'};

		}

		return {};

	}


	/**
	 * This is used by the Sport radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The 'code' value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isHighlighted(name: string): any {

		if (name === this.pForm.controls['code'].value) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * Things to do when the college changes.
	 *
	 * @param event The College object as an event.
	 */
	onCollegeChange(event:any): void {
		let college:College = new College(event);
		this.pForm.controls['college'].setValue(college.id);  // Sets the hidden value.
		this.player.college = college.id;
		this.player.college_name = college.name;

	}


	/**
	 * This is triggered when the league is changed.  It clears the position
	 * and team information.
	 *
	 * @param code  The new league code choice.
	 */
	onLeagueChange(code: string):void {

		this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
		this.pForm.controls['position'].setValue(0);
		this.pForm.controls['team'].setValue(-1);
		this.onSportChange(code);

	}


	/**
	 * When the user selects a Player, set the values
	 * as needed and retrieves the vendor information.
	 *
	 * @param event  The event that comes from the Player typeahead.
	 */
	onPlayerChange(event: any): void {
		let player: Player = new Player(event);
		this.pForm.controls['firstname'].setValue(player.firstname);
		this.pForm.controls['surname'].setValue(player.surname);
		this.pForm.controls['id'].setValue(player.id);
		this.pForm.controls['team'].setValue(player.team);
    this.pForm.controls['position'].setValue(player.position);
    this.pForm.controls['status'].setValue('active');
		this.pForm.controls['code'].setValue(player.code);
		this.pForm.controls['college'].setValue(player.college);
		this.pForm.controls['draft_year'].setValue((0 === player.draftyear) ? '' : player.draftyear);
		this.hideSearch = (-1 !== player.id);
		this.pForm.controls['create_edit'].setValue('existing');
    this.onSportChange(player.code);


		// This uses a reference to the Team typeahead to set the text box to the selected team title.
		this.teamTypeahead.typeaheadTeamInput.nativeElement.value = player.team_title;

		// This uses a reference to the College typeahead to set the text box to the selected college name.
		this.collegeTypeahead.typeaheadCollegeInput.nativeElement.value = player.college_name;

		// Clear the existing vendor information array.
		this.vendor.length = 0;

		// Start the retrieval of vendor data; return the Observer and process when data arrives.
		this.vendorFound = this.vendorService.getPlayerVendorData(player.id).subscribe(

			(response) => {

				if (Array.isArray(response)) {

					// Clear the old elements.
					this.vendor.length = 0;

					// Fill it with the new ones.
					for (let vendor of response) {

						this.vendor.push(new VendorLink(vendor));

					}

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);
					this.message.show();

				} else {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Vendor data format was incorrect';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			},

			(error) => {

				// Having no vendor data is allowed.
				if (error instanceof HttpErrorResponse && error.status === 404) {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);
					this.message.show();
					return;

				}

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = 'Error while loading vendor data';
				me.level = 4;
				this.message.add(me, true);
				this.message.show();

			}

		);

	}


	/**
	 * Change the Position pulldown when required.
	 *
	 * @param code  The new 'code' value to use when retrieving positions.
	 */
	onSportChange(code: string):void {

		// Clear the existing array.
		this.position.length = 0;

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.positionFound = this.positionService.getPositionsForSelect(code).subscribe(

			(response) => {

				if (Array.isArray(response)) {

					// Clear the old elements.
					this.position.length = 0;

					// Fill it with the new ones.
					for (let pos of response) {

						this.position.push(new Position(pos));

					}

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Ready';
					me.level = 2;
					this.message.add(me);
					this.message.show();

				} else {

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = 'Position data was incorrectly formatted';
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			},

			() => {

				// Notify the user.
				let me:MessageEntry = new MessageEntry();
				me.message = 'Error while retrieving position data';
				me.level = 4;
				this.message.add(me);
				this.message.show();

			}

		);

	}


	/**
	 * Handle the 'Save' or 'Save and Close' button click.
	 *
	 * @param {Object}  data  The information submitted with the page.
	 * @param {boolean} close Whether to close the modal upon success.
	 */
	onSubmit(data:object, close:boolean) {

		if ( true === data['valid'] ) {

			let name:string = ItemComponent.replaceWordCharacters(this.pForm.controls['firstname'].value + ' ' + this.pForm.controls['surname'].value );

			// Notify the user.
			let me:MessageEntry = new MessageEntry();
			me.message = 'Saving ' + name;
			me.level = 1;
			this.message.add(me, true);
			this.message.show();

			// Gather the data for output, removing the things that aren't needed.
			let json:object = {};
			json['surname'] = ItemComponent.replaceWordCharacters(data['value']['surname']);
			json['firstname'] = ItemComponent.replaceWordCharacters(data['value']['firstname']);
			json['code'] = data['value']['code'];
			json['team'] = data['value']['team'];
      json['position'] = data['value']['position'];
      json['status'] = data['value']['status'];
      json['vendors'] = this.vendor;

	    // The draft year value must be four digits or it doesn't get added to the submitted data.
      data['value']['draft_year'] = data['value']['draft_year'].toString();
			if (4 === data['value']['draft_year'].length) {
				json['draftyear'] = data['value']['draft_year'];
			}

			// If there's no college in the text field, put in the default value.
			if (0 === this.collegeTypeahead.typeaheadCollegeInput.nativeElement.value.trim().length) {
				json['college'] = PlayerAdminComponent.DEFAULT_COLLEGE_ID;
			} else {
				json['college'] = data['value']['college'];
			}

			// Build the query.
			let q:AngularQuery = new AngularQuery();
			q.useCacheBuster( false );
			q.useParams( false );
			let isNewPlayer:boolean = false;

			if ( -1 === data['value']['id'] ) {

				q.setEndpoint( AngularQuery.ENDPOINT_PLAYER, 'new' );
				isNewPlayer = true;

			} else {

				q.setEndpoint( AngularQuery.ENDPOINT_PLAYER, data['value']['id'].toString() );

			}

			// Unsubscribe from any existing player query.
			if (this.playerFound) {

				this.playerFound.unsubscribe();

			}

			// Start the retrieval of data; return the Observer and process when data arrives.
			this.playerFound = this.playerService.save( q.getUrl(), JSON.stringify( json ) ).subscribe(

				(response) => {

					// Close the window if the Save & Close button was clicked.
					if ( close ) {

						this.activeModal.close('Save and Close pressed');

					} else {

						this.player = new Player(response);
						this.pForm.patchValue(this.player);

						// Switch this from a 'create player' view to 'edit player' view.
						if (isNewPlayer) {

							this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = this.player.surname + ', ' + this.player.firstname;

						}

						// Notify the user.
						let me:MessageEntry = new MessageEntry();
						me.message = 'Saved ' + name;
						me.level = 2;
						this.message.add(me, true);
						this.message.show();
						this.clear();  // This clears the screen so the editor can do another player.

					}

				},

				(err) => {
					let msg:string = 'Unable to save ' + name;

					if (err instanceof HttpErrorResponse && err.status === 409) {

						msg = 'Unable to save ' + name + ' because that player already exists in this sport';

					}

					// Notify the user.
					let me:MessageEntry = new MessageEntry();
					me.message = msg;
					me.level = 4;
					this.message.add(me, true);
					this.message.show();

				}

			);

		}

	}


	/**
	 * When the user selects a Team, set the values
	 * as needed.
	 *
	 * @param event  The event that comes from the Team typeahead.
	 */
	onTeamChange(event: any): void {

		let team: Team = new Team(event);
		this.pForm.controls['team'].setValue(team.id);

	}


	/**
	 * Runs when the user has decided to replace the existing
	 * vendor entry with the one they are editing.
	 */
	replaceVendorEntry():void {

		this.editLink.vendor_link = this.vendorEditInput.nativeElement.value;
		this.vendor.splice(this.editIndex, 1, this.editLink);
		this.editVendorValue = false;
		this.vendorEditInput.nativeElement.value = '';

	}


	/**
	 * Toggle between the search and the
	 * create new choices.
	 *
	 * @param {string} name Whether this is a 'new' or 'existing' search.
	 */
	showHideSearch(name:string): void {

		this.pForm.controls['create_edit'].setValue(name);
		this.hideSearch = ('new' === name);
		this.clear();

	}


}



// WEBPACK FOOTER //
// ./src/app/admin/player/player-admin.component.ts